import "@fontsource-variable/orbitron";
import "./ui/style.css";
import { registerSW } from "virtual:pwa-register";
import Phaser from "phaser";
import {
	canvasSize,
	keepCanvasSharp,
	pixelRatio,
	setResolutionMode,
	toggleFullscreen,
	watchFrameRate,
} from "./game/display";
import { ASSETS_READY, GameFlow } from "./game/flow";
import { prefersTouch } from "./game/input/touch";
import { setMotionPreference } from "./game/motion";
import { BootScene } from "./game/scenes/boot-scene";
import { MatchScene } from "./game/scenes/match-scene";
import { PreloadScene } from "./game/scenes/preload-scene";
import { loadSettings } from "./game/settings";
import { isTypingTarget } from "./ui/dom";
import { GamepadMenuNav } from "./ui/gamepad-nav";
import { ScreenManager } from "./ui/screen-manager";

const container = document.getElementById("game-container");
const uiRoot = document.getElementById("ui-root");
if (!container || !uiRoot) throw new Error("#game-container / #ui-root missing from index.html");

const settings = loadSettings();
setResolutionMode(settings.resolution);
setMotionPreference(settings.reducedMotion);
const ratio = pixelRatio();
const initial = canvasSize(container.clientWidth, container.clientHeight, ratio);

const game = new Phaser.Game({
	type: Phaser.AUTO,
	title: "Gravity",
	parent: container,
	backgroundColor: "#000000",
	scale: {
		mode: Phaser.Scale.NONE,
		width: initial.width,
		height: initial.height,
		zoom: 1 / ratio,
		fullscreenTarget: "app",
	},
	scene: [BootScene, PreloadScene, MatchScene],
});

game.events.once(Phaser.Core.Events.READY, () => {
	keepCanvasSharp(game, container);
	watchFrameRate(game);
});

// Touch screens get on-screen Back buttons in menus (no Esc key); any touch switches them on.
document.body.dataset.coarse = String(prefersTouch());
window.addEventListener(
	"pointerdown",
	(ev) => {
		if (ev.pointerType === "touch") document.body.dataset.coarse = "true";
	},
	true,
);

const ui = new ScreenManager(uiRoot);
const flow = new GameFlow(game, ui);
new GamepadMenuNav(ui);
game.events.once(ASSETS_READY, () => flow.start());

// Offline play / installable app: the service worker exists only in production builds.
if (import.meta.env.PROD) {
	void registerSW({ immediate: true });
}

// Cross-engine determinism check (tests/e2e/determinism.spec.ts).
if (new URLSearchParams(window.location.search).has("probe")) {
	void import("./game/probe").then((m) => m.exposeProbe());
}

// F toggles fullscreen (a key press counts as the user gesture browsers require).
window.addEventListener("keydown", (ev) => {
	if (ev.code === "KeyF" && !ev.repeat && !isTypingTarget(ev)) toggleFullscreen(game);
});
